/**
 * SessionDataAdapter
 *
 * Normalizes workout session data from different sources (Garmin, Strava, Cedar in-app)
 * into a unified format for consistent display in WorkoutHistoryDisplay component.
 *
 * This adapter ensures that all session cards can display the same rich data,
 * regardless of the data source, using progressive disclosure for details.
 */

import type { CurrentVO2Max } from './canonical_types';

export interface UnifiedSessionData {
  // Core metadata
  id: string;
  sessionType: 'cardio' | 'strength' | 'mobility' | 'yoga' | 'hiit';
  source: 'garmin' | 'strava' | 'cedar' | 'apple_health' | 'android_health' | 'google_health' | 'manual';
  completedAt: Date;

  // Common metrics
  totalDuration: number; // seconds
  calories?: number;
  
  // Cardio-specific
  distance?: number; // meters
  avgPace?: number; // seconds per km
  avgSpeed?: number; // m/s
  maxSpeed?: number; // m/s
  avgHeartRate?: number;
  maxHeartRate?: number;
  /**
   * Per-activity HRV — Root Mean Square of Successive Differences.
   * Populated when the activity captured RR-interval data from a chest
   * strap that reports them (Polar H10, Garmin HRM-Pro, Wahoo TICKR).
   * NULL otherwise. Surfaces the +HRV affix on the HRM source badge
   * and (Tier 2/3) feeds the HRV-coupled load resolver. Migration 1338.
   */
  hrvRmssdMs?: number;
  /** Per-activity SDNN. Tier 1 reporting metric alongside RMSSD. */
  hrvSdnnMs?: number;
  /** Count of valid RR intervals that fed the HRV computation. <60 means HRV is null. */
  hrvSampleCount?: number;
  /**
   * Session RPE 1.0–10.0 — the athlete's own rating of how hard the whole
   * session felt. Migration 1574 added the columns and four capture paths
   * write them (completion screen, watch crown, chat extractor,
   * morning-nudge recovery ask), but until Sprint C
   * (execution-reconciliation, 2026-07-28) no session-data endpoint
   * SELECTed them — so the value never reached any surface. NULL when the
   * athlete never rated the session and Cedar couldn't infer one.
   */
  sessionRpeValue?: number | null;
  /**
   * Provenance of `sessionRpeValue`. 'user_post_workout' is a deliberate
   * tap and is authoritative; the other three are parsed/inferred and may
   * be corrected by the athlete. Reported honestly so the UI can present
   * an inference as an inference rather than as the athlete's own word.
   */
  sessionRpeSource?:
    | 'user_post_workout'
    | 'chat_extracted'
    | 'inferred_from_hr'
    | 'imported'
    | null;
  elevationGain?: number; // meters
  elevationLoss?: number; // meters (NEW for migration 179)
  
  // GPS data
  gpsData?: Array<{
    latitude: number;
    longitude: number;
    altitude?: number;
    timestamp: number;
    speed?: number;
    distanceFromStart?: number;
  }>;
  
  // Detailed samples (NEW for migration 179 - Garmin Detail / Strava Streams)
  samples?: Array<{
    timestamp: string;
    latitude?: number;
    longitude?: number;
    altitude?: number;
    speed?: number;
    heartRate?: number;
    power?: number;
    cadence?: number;
    distance?: number;
    grade?: number;
  }>;
  
  // Splits (for running/cycling)
  // 🔄 PHASE 4: extended to carry canonical CanonicalSplit fields — heart rate,
  // elevation gain/loss (NET deltas per split — not cumulative positive GPS
  // jitter), cadence, power. All optional; rendered conditionally by
  // ExpandableSplits.native.tsx via hasHR / hasElevation / hasCadence / hasPower.
  /** FIX-UNITS-1 C: the per-MILE splits (computed_splits.splits_standard), when served. */
  splitsStandard?: UnifiedSessionData['splits'];
  splits?: Array<{
    km: number;
    time: number; // seconds
    pace: number; // seconds/km
    type: 'auto' | 'manual';
    heartRate?: number;       // avg HR for the split (BPM)
    maxHeartRate?: number;    // max HR within the split window
    elevationGainM?: number;  // NET gain over split, meters, ≥0
    elevationLossM?: number;  // NET loss over split, meters, ≥0
    cadence?: number;         // avg SPM / RPM
    power?: number;           // avg watts
    // 🏔️ honest-terrain C2 (2026-08-21): grade-adjusted pace for this split, in
    // SECONDS PER KILOMETRE — the same unit as `pace`, so the two are directly
    // comparable and the renderer can put both through one sport-aware
    // formatter. Written by `services/computation/splits_engine.py` onto
    // `computed_splits.splits_metric[].gap_s_per_km`.
    //
    // OPTIONAL, AND ABSENT IS THE COMMON CASE. Every row written before the
    // sprint has no GAP at all, and a split with no elevation information never
    // gets one. Consumers MUST branch on presence — never coalesce to 0, never
    // render a placeholder (`ExpandableSplits.native.tsx` drops the whole
    // column when no split carries a meaningful one).
    gapSPerKm?: number;
    // 🩺 Apple-Watch-run-parity P2 (2026-07-22): phase-lap provenance.
    // `convert_actual_phases_to_lap_splits` (in_app_activity_bridge.py) emits
    // per-phase "laps" with split_type='phase', distance_meters=null,
    // pace=null. Without these, phase laps rendered as fabricated km/pace rows.
    // The renderer keys on splitType==='phase' to show phase label + duration +
    // HR and hide the meaningless km/pace columns.
    splitType?: string;       // 'phase' for plan-phase laps (else undefined)
    phaseName?: string;       // e.g. "Warmup", "Steady", "Cooldown"
    phaseType?: string;       // e.g. "warmup", "steady", "cooldown"
  }>;
  
  // Phase data (structured workouts)
  phases?: Array<{
    phaseNumber: number;
    phaseType: string;
    prescribedPace?: number;
    actualPace?: number;
    prescribedDuration?: number;
    actualDuration?: number;
    prescribedDistance?: number;
    actualDistance?: number;
    avgHR?: number;
    maxHR?: number;
    adherence?: number;
    instructions?: string;
  }>;
  
  // Strength/HIIT-specific
  exercises?: Array<{
    exerciseName: string;
    prescribedSets: number;
    prescribedReps: string | number;
    prescribedWeight?: number;
    actualSets: number;
    actualReps: number[];
    actualWeight: number[];
    /** Total tracked elapsed seconds across all rounds (timer-completed only). */
    actualDuration?: number;
    restTimes?: number[];
    videoUrl?: string;
    completed: boolean;
    instructions?: string[];
    formCues?: string[];
  }>;
  
  // HIIT-specific
  hiitType?: 'circuit' | 'emom' | 'amrap';
  rounds?: number;
  circuits?: Array<{
    circuitNumber: number;
    totalRounds: number;
    completedRounds: number;
    exercises: Array<{
      name: string;
      reps: string | number;
      duration?: number;
      completed: boolean;
    }>;
    restBetweenRounds?: number;
  }>;
  emom?: {
    totalMinutes: number;
    completedMinutes: number;
    exercises: Array<{ name: string; reps: string; minuteAssignment?: number }>;
    avgTimePerMinute?: number;
  };
  amrap?: {
    totalDuration: number;
    roundsCompleted: number;
    exercises: Array<{ name: string; reps: string }>;
    roundTimes?: number[];
  };
  
  // Power/Cadence (cycling/running)
  avgPower?: number;
  maxPower?: number;
  avgCadence?: number;  // NEW explicit field for migration 179
  maxCadence?: number;  // NEW explicit field for migration 179
  normalizedPower?: number;

  // 🔄 PHASE 5: per-activity HR zone time breakdown (seconds per zone) from
  // unified_athletic_data.hr_zones_distribution (populated by pipeline
  // compute stage). Consumed by ZoneTimeBreakdown.native.tsx under the HR
  // stat card in ExpandableActivityTimeline.
  hrZonesDistribution?: {
    zone_system?: 'cedar' | 'garmin';
    zone_1_seconds: number;
    zone_2_seconds: number;
    zone_3_seconds: number;
    zone_4_seconds: number;
    zone_5_seconds: number;
    below_zone_1_seconds?: number;
    above_zone_5_seconds?: number;
    zone_1_min_bpm?: number | null;
    zone_1_max_bpm?: number | null;
    zone_2_min_bpm?: number | null;
    zone_2_max_bpm?: number | null;
    zone_3_min_bpm?: number | null;
    zone_3_max_bpm?: number | null;
    zone_4_min_bpm?: number | null;
    zone_4_max_bpm?: number | null;
    zone_5_min_bpm?: number | null;
    zone_5_max_bpm?: number | null;
    max_heart_rate_used?: number | null;
    resting_heart_rate_used?: number | null;
  };

  // 🔄 Decision #5: power zones distribution (Coggan 7-zone, % of FTP).
  // Populated for activities with power data when user has FTP configured.
  powerZonesDistribution?: {
    ftp_watts?: number | null;
    zone_1_seconds: number;
    zone_2_seconds: number;
    zone_3_seconds: number;
    zone_4_seconds: number;
    zone_5_seconds: number;
    zone_6_seconds: number;
    zone_7_seconds: number;
  };

  // 🔄 Decision #5: pace zones distribution (absolute 5-band, matches
  // splits_engine PACE_ZONE_BOUNDARIES). Populated for activities with
  // running/walking speed samples.
  /**
   * Time-in-band. TWO SHAPES share this column, and they are not variants of
   * one model — they band on different quantities in different units.
   *
   * RUN (`compute_pace_zones_distribution`): ABSOLUTE seconds-per-KILOMETRE
   * thresholds, keyed `zone_1..5`.
   *
   * SWIM (`services/computation/swim_pace_buckets.py`): bands RELATIVE to the
   * athlete's own Critical Swim Speed, in seconds-per-100-METRES, keyed by
   * name. A swim has to be banded this way — every swimmer's pace falls into
   * ONE absolute sec/km bucket, which is a distribution that says nothing.
   *
   * `unit` is stated by the producer and is the discriminator. Never sniff the
   * keys: the two numbers look identical in a debugger and a consumer that
   * assumes sec/km renders every swim band ten times too fast.
   */
  // 🏊 BE-10 swim mechanics. Served by BOTH completed-session endpoints since
  // 2026-09; `active_lengths` (migration 1023) had ZERO readers anywhere
  // before that. Absent for every non-swim, and NULL means "not recorded" —
  // never render a zero for one (contract §8.9).
  swimSwolf?: number;
  /** TOTAL strokes for the activity (migration 1013's own words), not per length. */
  swimStrokes?: number;
  swimStrokeType?: string;
  poolLengthM?: number;
  activeLengths?: number;

  paceZonesDistribution?: {
    // Run shape.
    zone_1_seconds: number;
    zone_2_seconds: number;
    zone_3_seconds: number;
    zone_4_seconds: number;
    zone_5_seconds: number;
    zone_1_pace_s_per_km?: number | null;
    zone_2_pace_s_per_km?: number | null;
    zone_3_pace_s_per_km?: number | null;
    zone_4_pace_s_per_km?: number | null;
    zone_5_pace_s_per_km?: number | null;
    // Swim shape (BE-10).
    unit?: string;
    css_sec_per_100m?: number;
    intervals_counted?: number;
    sprint_seconds?: number;
    vo2_seconds?: number;
    threshold_seconds?: number;
    aerobic_seconds?: number;
    recovery_seconds?: number;
  };

  /**
   * 🏊 D6 (2026-09-21): a swim's CSS-relative bands, in their OWN field. Set
   * only when the producer stated `unit: 'sec_per_100m'`. Kept out of
   * `paceZonesDistribution` on purpose — that field feeds the activity
   * timeline and ZoneTimeBreakdown, which read the per-km run shape and would
   * drop the HR fallback if handed a swim. Read by the completed drawer only.
   */
  swimPaceZonesDistribution?: {
    unit?: string;
    css_sec_per_100m?: number;
    intervals_counted?: number;
    sprint_seconds?: number;
    vo2_seconds?: number;
    threshold_seconds?: number;
    aerobic_seconds?: number;
    recovery_seconds?: number;
  };
  
  // Training zones — array format matching ExpandableHeartRateZones component
  hrZones?: Array<{
    zone: number;
    name: string;
    minHR: number;
    maxHR: number;
    timeInZone: number;
    percentageOfTime: number;
  }> | any;
  
  powerZones?: {
    zone1: number;
    zone2: number;
    zone3: number;
    zone4: number;
    zone5: number;
    zone6: number;
    zone7: number;
  };
  
  // Platform-specific metrics (NEW for migration 179)
  trainingEffect?: number;  // Garmin only (0.0-5.0)
  // Per-activity vo2_max_estimate is no longer surfaced (April 2026 device-import
  // parity mission). Use `currentVo2Max` instead — resolver-derived rolling value
  // with source provenance (Garmin > Apple > HC > Fitbit > 30d EWMA fallback).
  currentVo2Max?: CurrentVO2Max | null;
  sufferScore?: number;  // Strava only
  stravaActivityId?: number;  // For "View on Strava" deep links
  
  // Strava segments (NEW for migration 179)
  segments?: Array<{
    segmentId: number;
    segmentName: string;
    elapsedTime: number;
    movingTime?: number;
    distance: number;
    averageSpeed: number;
    maxSpeed?: number;
    averageHeartRate?: number;
    maxHeartRate?: number;
    prRank?: number;  // Personal record rank
    komRank?: number;  // King/Queen of Mountain rank
    leaderboardPosition?: number;
    totalElevationGain?: number;
  }>;
  
  // Running dynamics (Garmin-specific, from migration 1013)
  groundContactTimeMs?: number;    // Ground contact time in milliseconds
  verticalOscillationCm?: number;  // Vertical oscillation in centimeters
  strideLengthM?: number;          // Stride length in meters
  groundContactBalancePct?: number; // Ground contact balance percentage

  // Detailed metrics (NEW for migration 179 - parsed JSON blob)
  detailedMetrics?: any;

  // Device attribution fields (Mission 6 - Brand Compliance)
  deviceModel?: string;        // "Forerunner 965", "Fenix 7"
  deviceManufacturer?: string; // "Garmin", "Apple", "Polar"
  deviceName?: string;         // "John's Forerunner 965" (user-defined, optional)

  // Source attribution — the actual app that recorded the workout
  sourceAppName?: string;    // e.g. "Peloton", "Runna", "Nike Run Club" (from HealthKit sourceRevision / Health Connect metadata)
  sourcePackageName?: string; // e.g. "com.onepeloton.callisto" (package identifier)

  // Optional fields from API / post-completion UI (dataRichnessHelpers compatibility)
  type?: string;
  activityType?: string;
  cedarInsights?: Array<{ summary?: string; bullets?: string[] }>;
  userFeedback?: string | Record<string, unknown>;
  perceivedEffort?: number;
  completionNotes?: string;
  dataSource?: string;
  duration?: number;           // seconds (alias for totalDuration in some API responses)
  watchPushStatus?: string;
  watchPlatform?: string;

  // W6 (2026-04-28): unified_athletic_data.data_completeness_tier surfaced
  // on the session so the workout card can render a "summary data" badge
  // when the activity is bronze-tier (no time-series, summary-only ingest).
  // Refs: docs/plans/cross-device-pipeline-parity-sprint.md W6
  dataCompletenessTier?: 'platinum' | 'gold' | 'silver' | 'bronze' | 'basic' | null;

  // W11 (2026-04-28): activity-level running dynamics averages.
  // Populated by W9 sample-aggregator on the backend; surfaced on the
  // workout card grid as "Vertical bounce / Foot contact time /
  // Stride length / L/R balance / Vertical ratio" tiles.
  // Source: Garmin HRM-Pro per-sample → backend avg, OR Apple Watch
  // S7+ per-sample → backend avg. Strava activities don't expose
  // running dynamics (API limitation) so these stay null cleanly.
  // Refs: docs/plans/cross-device-pipeline-parity-sprint.md W11
  avgVerticalOscillationCm?: number | null;
  avgGroundContactTimeMs?: number | null;
  avgStrideLengthM?: number | null;
  avgGroundContactBalancePct?: number | null;
  avgVerticalRatioPct?: number | null;

  // 🌤️ Observed session weather (2026-07 audit, Part 2). Real conditions
  // captured at ingestion for an OUTDOOR activity (unified_athletic_data.
  // weather_conditions). Present only when the backend flag is on AND a
  // freshness-guarded snapshot was stored — otherwise absent (card hides).
  observedWeather?: ObservedSessionWeather | null;

  // 🧗 Climbing route vertical — the phone barometer's measure of the CLIMB
  // (unified_athletic_data.raw_data.route_vertical, written by
  // services/route_vertical.py). Deliberately separate from elevationGain /
  // elevationLoss, which on an outdoor day are the GPS APPROACH. Absent for
  // every non-climbing activity; null when nothing measurable was recorded.
  routeVertical?: RouteVerticalSummary | null;
}

/** Route vertical in metres (finite, >= 0). A side is null when it was not
 *  measured — never 0 standing in for "missing". */
export interface RouteVerticalSummary {
  ascentM: number | null;
  descentM: number | null;
  /** Share of the recording the barometer sampled (0..1); null when not reported. A
   *  locked screen stops the sensor, so a low value means a partial measurement. */
  coverage: number | null;
  /** The workout card's environment. Outdoors the phone also counts the approach hike. */
  environment: 'indoor' | 'outdoor' | 'mixed' | null;
}

/** Canonical observed-weather snapshot mirrored from the backend
 *  (services/session_weather_snapshot.py). All measurement fields are
 *  nullable so a partial reading never fabricates a value. */
export interface ObservedSessionWeather {
  tempC: number | null;
  apparentTempC: number | null;
  condition: string | null;
  conditionDescription: string | null;
  conditionIcon: string | null;
  humidityPct: number | null;
  windKmh: number | null;
  /** 'activity_gps' (from the activity's own GPS) or 'profile_location'
   *  (the user's stored home location, used for GPS-less manual walks). */
  locationSource: string | null;
  provider: string | null;
  observedAt: string | null;
  capturedLagMinutes: number | null;
  /** Minutes AFTER the activity ended that the reading was captured. ~0 for a
   *  real-time completion; large when a device synced late. Drives whether the
   *  card says conditions "during" vs "around" the session. */
  capturedAfterEndMinutes: number | null;
}

export class SessionDataAdapter {
  /**
   * Normalizes raw lap/split data from multiple API formats into UnifiedSessionData.splits shape.
   *
   * Sources use inconsistent field names:
   *  - Garmin DB:  { split_number, distance_km, time_seconds, pace_per_km, ... }
   *  - Strava:     { split_number, distance, elapsed_time, moving_time, average_speed, ... }
   *  - Cedar timer: { km, time, pace, type }   ← already correct
   *  - Apple/Android: same as Garmin DB after universal ingester normalizes
   */
  static normalizeSplits(raw: any[] | null | undefined): UnifiedSessionData['splits'] {
    if (!raw || !Array.isArray(raw) || raw.length === 0) return undefined;
    const result: NonNullable<UnifiedSessionData['splits']> = [];
    for (const s of raw) {
      // Already in correct shape (Cedar timer / previously normalized)
      if (typeof s.km === 'number' && typeof s.pace === 'number') {
        result.push({
          km: s.km,
          time: s.time ?? 0,
          pace: s.pace,
          type: s.type === 'manual' ? 'manual' : 'auto',
        });
        continue;
      }
      // Garmin DB / Apple Health / Android Health format
      const km = s.distance_km ?? s.split_distance_km ?? (s.distance_meters ? s.distance_meters / 1000 : undefined);
      const time = s.time_seconds ?? s.duration_seconds ?? s.elapsed_time ?? s.moving_time ?? s.total_time_seconds;
      // Pace: prefer pre-computed, else derive from distance/time
      // Guard: Garmin stores avg_pace_sec_per_km in minutes (e.g. 5.25 = 5:15/km).
      // Detect minutes by value < 30 (no human runs sub-30-sec/km) and convert to seconds.
      const rawPace = s.pace_per_km ?? s.average_pace_seconds_per_km ?? s.avg_pace_sec_per_km;
      const pace = rawPace !== undefined
        ? (typeof rawPace === 'number' && rawPace < 30 ? rawPace * 60 : rawPace)
        : (time && km && km > 0 ? time / km : undefined);
      if (typeof km === 'number' && !isNaN(km) && typeof pace === 'number' && !isNaN(pace)) {
        result.push({
          km,
          time: time ?? 0,
          pace,
          type: 'auto',
        });
      }
    }
    return result.length > 0 ? result : undefined;
  }

  /**
   * Adapts Garmin activity data from unified_athletic_data schema
   * 🆕 Enhanced for migration 179 - Garmin Activity Detail support
   */
  static fromGarmin(garminData: any): UnifiedSessionData {
    // 🔧 FEB 2, 2026: Support BOTH backend field names (snake_case from DB) AND legacy names
    // Backend returns: average_heart_rate, calories_burned, average_pace_seconds_per_km, etc.
    // Legacy code may still use: avg_heart_rate, calories, avg_pace_sec_per_km
    
    // Parse JSON fields - support both field names
    const hrData = garminData.hr_data ? (typeof garminData.hr_data === 'string' ? JSON.parse(garminData.hr_data) : garminData.hr_data) : null;
    const gpsData = garminData.trackpoints || garminData.gps_trackpoints ? 
      (typeof (garminData.trackpoints || garminData.gps_trackpoints) === 'string' ? 
        JSON.parse(garminData.trackpoints || garminData.gps_trackpoints) : 
        (garminData.trackpoints || garminData.gps_trackpoints)) : null;
    const splits = garminData.lap_data || garminData.lap_splits ? 
      (typeof (garminData.lap_data || garminData.lap_splits) === 'string' ? 
        JSON.parse(garminData.lap_data || garminData.lap_splits) : 
        (garminData.lap_data || garminData.lap_splits)) : null;
    const powerData = garminData.power_data ? (typeof garminData.power_data === 'string' ? JSON.parse(garminData.power_data) : garminData.power_data) : null;
    
    // 🆕 Parse NEW fields from migration 179
    const samples = garminData.samples ? (typeof garminData.samples === 'string' ? JSON.parse(garminData.samples) : garminData.samples) : null;
    const detailedMetrics = garminData.detailed_metrics ? (typeof garminData.detailed_metrics === 'string' ? JSON.parse(garminData.detailed_metrics) : garminData.detailed_metrics) : null;
    
    return {
      id: garminData.id,
      sessionType: this.mapGarminActivityType(garminData.activity_type),
      source: 'garmin',
      completedAt: new Date(garminData.activity_date || garminData.start_time || garminData.created_at),
      totalDuration: garminData.duration_seconds || 0,
      // 🔧 Support both field names: calories_burned (backend) OR calories (legacy)
      calories: garminData.calories_burned ?? garminData.calories,
      distance: garminData.distance_meters,
      // 🔧 Support all pace field names: average_pace_seconds_per_km (backend),
      // avg_pace_sec_per_km (legacy), pace_per_km_seconds (DB column name)
      avgPace: garminData.average_pace_seconds_per_km ?? garminData.avg_pace_sec_per_km ?? garminData.pace_per_km_seconds,
      // 🔧 Support both: average_speed_ms (backend) OR avg_speed_m_per_sec (legacy)
      avgSpeed: garminData.average_speed_ms ?? garminData.avg_speed_m_per_sec,
      maxSpeed: garminData.max_speed_ms ?? garminData.max_speed_m_per_sec,
      // 🔧 Support both: average_heart_rate (backend) OR avg_heart_rate (legacy)
      avgHeartRate: garminData.average_heart_rate ?? garminData.avg_heart_rate ?? hrData?.avg_hr,
      maxHeartRate: garminData.max_heart_rate ?? hrData?.max_hr,
      // HRV: chest-strap RMSSD/SDNN from Cedar timer (migration 1338).
      hrvRmssdMs: garminData.hrv_rmssd_ms,
      hrvSdnnMs: garminData.hrv_sdnn_ms,
      hrvSampleCount: garminData.hrv_sample_count,
      elevationGain: garminData.elevation_gain_meters,
      elevationLoss: garminData.elevation_loss_meters,
      // 🔧 FIX: Normalize trackpoint field names — ENRICH path stored lat/lng,
      // frontend expects latitude/longitude. Also handles future-correct data.
      // 🔧 FIX 3 (2026-04-29): Garmin canonical trackpoint shape (post W4 sprint)
      // uses suffixed unit names — `altitude_m`, `timestamp_s`, `speed_mps`,
      // `distance_m`, `heart_rate_bpm`, `cadence_spm`. The previous mapping read
      // bare names only, which returned undefined for canonical Garmin data.
      // Polyline still rendered (lat/lng works) but timeline tooltips, distance
      // markers, HR series, and cadence silently degraded. Read both forms.
      gpsData: gpsData ? gpsData.map((p: any) => ({
        latitude: p.latitude ?? p.lat,
        longitude: p.longitude ?? p.lng,
        altitude: p.altitude ?? p.altitude_m,
        timestamp: p.timestamp ?? p.timestamp_s,
        speed: p.speed ?? p.speed_mps,
        distanceFromStart: p.distanceFromStart ?? p.distance ?? p.distance_m,
        heartRate: p.heartRate ?? p.heart_rate ?? p.heart_rate_bpm,
        cadence: p.cadence ?? p.cadence_spm,
      })) : null,
      // 🔧 FIX: Normalize raw Garmin sample field names to UnifiedSessionData schema.
      // ENRICH path stores samples verbatim from Garmin API (speedMetersPerSecond,
      // stepsPerMinute, elevationInMeters, etc.) — normalize to standard names.
      // 🔧 FIX 3b (2026-06-30): also read canonical suffixed keys. The Garmin
      // canonical migration (2026-04-16) made the backend persist samples with
      // timestamp_s / heart_rate_bpm / speed_mps / cadence_spm / altitude_m /
      // distance_m / power_watts. This (web-only) samples mapper still read
      // legacy camelCase only, so every field came back undefined and the web
      // time-series chart went blank for canonical Garmin rows. Mirrors the
      // gpsData/trackpoints block above (FIX 3, 2026-04-29) — the samples branch
      // was missed then. timestamp_s preserved so the chart's canonical-first
      // getTimestamp() resolves.
      samples: samples ? samples.map((s: any) => ({
        timestamp_s: s.timestamp_s,
        timestamp: s.startTimeInSeconds ?? s.clockDurationInSeconds ?? s.timestamp ?? s.timestamp_s,
        heartRate: s.heartRate ?? s.heart_rate ?? s.heart_rate_bpm,
        speed: s.speedMetersPerSecond ?? s.speed ?? s.speed_mps,
        cadence: s.stepsPerMinute ?? s.cadenceInStepsPerMinute ?? s.cadence ?? s.cadence_spm,
        altitude: s.elevationInMeters ?? s.altitude ?? s.altitude_m,
        latitude: s.latitudeInDegree ?? s.latitude,
        longitude: s.longitudeInDegree ?? s.longitude,
        distance: s.totalDistanceInMeters ?? s.distanceInMeters ?? s.distance ?? s.distance_m,
        power: s.power ?? s.directWatts ?? s.power_watts,
        temperature: s.airTemperatureCelcius ?? s.temperature ?? s.temperature_c,
      })) : null,
      detailedMetrics: detailedMetrics,
      splits: SessionDataAdapter.normalizeSplits(splits),
      avgPower: garminData.average_power_watts ?? powerData?.avg_power_watts,
      maxPower: garminData.max_power_watts ?? powerData?.max_power_watts,
      normalizedPower: powerData?.normalized_power_watts,
      avgCadence: garminData.average_cadence ?? garminData.avg_cadence,
      maxCadence: garminData.max_cadence,
      // 🆕 Garmin-specific metrics (NEW for migration 179)
      trainingEffect: garminData.training_effect,
      // Per-activity vo2_max_estimate dropped April 2026; resolver value lives at
      // root of API response as `current_vo2_max` (Garmin's daily rolling number,
      // Apple/HC/Fitbit fallback, 30d EWMA last resort).
      currentVo2Max: garminData.current_vo2_max ?? null,
      // 🔧 FIX: Read heart_rate_zones from API response (not hr_data which doesn't exist).
      // DB stores: {"zones": {"zone_1": {"min":125,"max":138,"name":"Recovery"}, ...},
      //             "zone_distribution": {"zone_1": 100, ...}}
      // Component expects: [{zone:1, name:"Recovery", minHR:125, maxHR:138, percentageOfTime:100}, ...]
      hrZones: (() => {
        const hrzRaw = garminData.heart_rate_zones;
        const hrzData = hrzRaw
          ? (typeof hrzRaw === 'string' ? JSON.parse(hrzRaw) : hrzRaw)
          : null;
        // 🩺 Apple-Watch-run-parity P1 (2026-07-22): suppress the elite engine's
        // DISPLAY-ONLY stub (`_display_only:true`, single avg HR bucketed to one
        // zone at 100% — elite_athletic_analysis_engine.py:1373-1398). Mirrors the
        // guard in WorkoutDetail.native.tsx's hrZones builder. Rendering the stub
        // fabricates "100% Zone N" for the zero-sample cohort.
        if (hrzData?._display_only) return hrData?.zones ?? null;
        if (hrzData?.zones && hrzData?.zone_distribution) {
          const built = Object.entries(hrzData.zones).map(([key, zone]: [string, any]) => {
            const zoneNum = parseInt(key.replace('zone_', ''));
            return {
              zone: zoneNum,
              name: zone.name,
              minHR: zone.min,
              maxHR: zone.max,
              timeInZone: 0,
              percentageOfTime: hrzData.zone_distribution?.[key] ?? 0,
            };
          }).sort((a: any, b: any) => a.zone - b.zone);
          // Reject a degenerate single-zone-at-100 (the fabrication signature).
          const nonZeroZones = built.filter((z) => (z.percentageOfTime ?? 0) > 0);
          const isDegenerate =
            nonZeroZones.length <= 1
            && nonZeroZones.some((z) => (z.percentageOfTime ?? 0) >= 99);
          if (isDegenerate) return hrData?.zones ?? null;
          return built;
        }
        return hrData?.zones ?? null;
      })(),
      powerZones: powerData?.zones,
      // Device attribution fields
      deviceModel: garminData.device_model,
      deviceManufacturer: garminData.device_manufacturer || 'Garmin',
      deviceName: garminData.device_name,
      // W6 (2026-04-28): surface tier so the workout card grid can
      // render "summary data" badge on bronze-tier rows.
      dataCompletenessTier: garminData.data_completeness_tier ?? null,
      // W11 (2026-04-28): running dynamics averages from W9 backend
      // sample-aggregator. Populated for Garmin HRM-Pro / HRM-Run
      // activities where the per-sample stream had these fields.
      avgVerticalOscillationCm: garminData.avg_vertical_oscillation_cm ?? null,
      avgGroundContactTimeMs: garminData.avg_ground_contact_time_ms ?? null,
      avgStrideLengthM: garminData.avg_stride_length_m ?? null,
      avgGroundContactBalancePct: garminData.avg_ground_contact_balance_pct ?? null,
      avgVerticalRatioPct: garminData.avg_vertical_ratio_pct ?? null,
    };
  }

  /**
   * Adapts Strava activity data from unified_athletic_data schema
   * 🆕 Enhanced for migration 179 - Strava Streams support
   */
  static fromStrava(stravaData: any): UnifiedSessionData {
    // Parse JSON fields
    const hrData = stravaData.hr_data ? (typeof stravaData.hr_data === 'string' ? JSON.parse(stravaData.hr_data) : stravaData.hr_data) : null;
    // Support BOTH field names: 'trackpoints' (DB column name) and 'gps_trackpoints' (legacy)
    const rawGps = stravaData.trackpoints || stravaData.gps_trackpoints;
    const gpsData = rawGps ? (typeof rawGps === 'string' ? JSON.parse(rawGps) : rawGps) : null;
    const splits = stravaData.lap_splits ? (typeof stravaData.lap_splits === 'string' ? JSON.parse(stravaData.lap_splits) : stravaData.lap_splits) : null;
    const powerData = stravaData.power_data ? (typeof stravaData.power_data === 'string' ? JSON.parse(stravaData.power_data) : stravaData.power_data) : null;
    
    // 🆕 Parse NEW fields from migration 179
    const samples = stravaData.samples ? (typeof stravaData.samples === 'string' ? JSON.parse(stravaData.samples) : stravaData.samples) : null;
    const detailedMetrics = stravaData.detailed_metrics ? (typeof stravaData.detailed_metrics === 'string' ? JSON.parse(stravaData.detailed_metrics) : stravaData.detailed_metrics) : null;
    const segments = stravaData.segments ? (typeof stravaData.segments === 'string' ? JSON.parse(stravaData.segments) : stravaData.segments) : null;
    
    return {
      id: stravaData.id,
      sessionType: this.mapStravaActivityType(stravaData.activity_type),
      source: 'strava',
      completedAt: new Date(stravaData.activity_date || stravaData.created_at),
      totalDuration: stravaData.duration_seconds || 0,
      calories: stravaData.calories_burned ?? stravaData.calories,
      distance: stravaData.distance_meters,
      avgPace: stravaData.avg_pace_sec_per_km ?? stravaData.average_pace_seconds_per_km ?? stravaData.pace_per_km_seconds,
      avgSpeed: stravaData.avg_speed_m_per_sec,
      maxSpeed: stravaData.max_speed_m_per_sec,
      avgHeartRate: stravaData.avg_heart_rate ?? hrData?.avg_hr,  // 🆕 Prefer direct field
      maxHeartRate: stravaData.max_heart_rate ?? hrData?.max_hr,  // 🆕 Prefer direct field
      // HRV: chest-strap RMSSD/SDNN from Cedar timer (migration 1338).
      hrvRmssdMs: stravaData.hrv_rmssd_ms,
      hrvSdnnMs: stravaData.hrv_sdnn_ms,
      hrvSampleCount: stravaData.hrv_sample_count,
      elevationGain: stravaData.elevation_gain_meters,
      elevationLoss: stravaData.elevation_loss_meters,  // 🆕 Migration 179
      // S5-1: Normalize GPS point field names (lat/lng → latitude/longitude) — matches Apple/Android/Garmin adapters
      gpsData: gpsData ? gpsData.map((p: any) => ({
        latitude: p.latitude ?? p.lat,
        longitude: p.longitude ?? p.lng ?? p.lon,
        altitude: p.altitude ?? p.alt ?? p.elevation,
        timestamp: p.timestamp ?? p.time,
        speed: p.speed ?? p.velocity,
        distanceFromStart: p.distanceFromStart ?? p.distance_from_start ?? p.distance,
      })) : null,
      // 🆕 NEW for migration 179 - Strava Streams samples
      samples: samples,
      detailedMetrics: detailedMetrics,
      // 🆕 NEW for migration 179 - Strava segment efforts
      segments: segments,
      splits: SessionDataAdapter.normalizeSplits(splits),
      avgPower: stravaData.average_power_watts ?? powerData?.avg_power_watts,  // 🆕 Prefer direct field
      maxPower: stravaData.max_power_watts ?? powerData?.max_power_watts,  // 🆕 Prefer direct field
      normalizedPower: powerData?.normalized_power_watts,
      avgCadence: stravaData.average_cadence ?? stravaData.avg_cadence,  // 🆕 Migration 179
      maxCadence: stravaData.max_cadence,  // 🆕 Migration 179
      // 🆕 Strava-specific metrics (NEW for migration 179)
      sufferScore: stravaData.suffer_score,
      stravaActivityId: stravaData.strava_activity_id,
      hrZones: hrData?.zones,
      powerZones: powerData?.zones,
      // Device attribution fields (may be present if Garmin device via Strava)
      deviceModel: stravaData.device_model,
      deviceManufacturer: stravaData.device_manufacturer,
      deviceName: stravaData.device_name,
      // W6 (2026-04-28)
      dataCompletenessTier: stravaData.data_completeness_tier ?? null,
      // W11 (2026-04-28): running dynamics — null for native Strava
      // activities (API doesn't expose them); populated only when the
      // activity originated on a Garmin device that synced through to
      // Strava with dynamics preserved.
      avgVerticalOscillationCm: stravaData.avg_vertical_oscillation_cm ?? null,
      avgGroundContactTimeMs: stravaData.avg_ground_contact_time_ms ?? null,
      avgStrideLengthM: stravaData.avg_stride_length_m ?? null,
      avgGroundContactBalancePct: stravaData.avg_ground_contact_balance_pct ?? null,
      avgVerticalRatioPct: stravaData.avg_vertical_ratio_pct ?? null,
    };
  }

  /**
   * Adapts Cedar in-app workout session data
   */
  static fromCedar(cedarData: any): UnifiedSessionData {
    const sessionData = cedarData.sessionData || cedarData;

    // 🔧 FIX: Support BOTH field names for GPS data — 'trackpoints' (DB column via API)
    // and 'gpsData' (legacy/direct). Matches the pattern used by fromGarmin() and fromAppleHealth().
    const rawGps = sessionData.trackpoints || sessionData.gps_trackpoints || sessionData.gpsData;
    const parsedGps = rawGps ? (typeof rawGps === 'string' ? (() => { try { return JSON.parse(rawGps); } catch { return null; } })() : rawGps) : null;

    return {
      id: cedarData.id ?? sessionData.id,
      // S5-5: Use ?? instead of || to preserve falsy-but-valid values (e.g., totalDuration: 0)
      sessionType: sessionData.type ?? 'cardio',
      source: 'cedar',
      completedAt: new Date(cedarData.completedAt ?? cedarData.completed_at),
      totalDuration: sessionData.totalDuration ?? 0,
      calories: sessionData.calories,
      distance: sessionData.distance,
      avgPace: sessionData.avgPace,
      avgSpeed: sessionData.avgSpeed,
      maxSpeed: sessionData.maxSpeed,
      avgHeartRate: sessionData.avgHeartRate,
      maxHeartRate: sessionData.maxHeartRate,
      // HRV: chest-strap RMSSD/SDNN from Cedar timer (migration 1338).
      hrvRmssdMs: sessionData.hrvRmssdMs ?? sessionData.hrv_rmssd_ms,
      hrvSdnnMs: sessionData.hrvSdnnMs ?? sessionData.hrv_sdnn_ms,
      hrvSampleCount: sessionData.hrvSampleCount ?? sessionData.hrv_sample_count,
      elevationGain: sessionData.elevationGain,
      elevationLoss: sessionData.elevationLoss,
      // S5-4: Normalize GPS data for Cedar-sourced sessions
      // 🔧 FIX 3 (2026-04-29): Read canonical-suffix keys too (altitude_m,
      // timestamp_s, speed_mps, distance_m, heart_rate_bpm, cadence_spm) so
      // any path that emits canonical shape doesn't lose those fields.
      gpsData: parsedGps ? (Array.isArray(parsedGps) ? parsedGps.map((p: any) => ({
        latitude: p.latitude ?? p.lat,
        longitude: p.longitude ?? p.lng,
        altitude: p.altitude ?? p.altitude_m,
        timestamp: p.timestamp ?? p.timestamp_s,
        speed: p.speed ?? p.speed_mps,
        distanceFromStart: p.distanceFromStart ?? p.distance_from_start ?? p.distance ?? p.distance_m,
        heartRate: p.heartRate ?? p.heart_rate ?? p.heart_rate_bpm,
        cadence: p.cadence ?? p.cadence_spm,
      })) : null) : null,
      splits: SessionDataAdapter.normalizeSplits(sessionData.splits),
      phases: sessionData.phases,
      exercises: sessionData.exercises,
      hiitType: sessionData.hiitType,
      circuits: sessionData.circuits,
      emom: sessionData.emom,
      amrap: sessionData.amrap,
      avgPower: sessionData.avgPower ?? sessionData.avg_power,
      maxPower: sessionData.maxPower ?? sessionData.max_power,
      avgCadence: sessionData.avgCadence ?? sessionData.avg_cadence,
      maxCadence: sessionData.maxCadence ?? sessionData.max_cadence,
      hrZones: sessionData.hrZones ?? sessionData.hr_zones,
      // Device attribution
      deviceModel: sessionData.deviceModel ?? sessionData.device_model,
      deviceManufacturer: sessionData.deviceManufacturer ?? sessionData.device_manufacturer,
      sourceAppName: sessionData.sourceAppName ?? sessionData.source_app_name,
      // W6 (2026-04-28)
      dataCompletenessTier: sessionData.data_completeness_tier ?? sessionData.dataCompletenessTier ?? null,
      // W11 (2026-04-28): Cedar timer running dynamics. Populated when
      // a paired BLE / phone-sensor stream provided these per-sample;
      // BLE HRM activation will surface them too.
      avgVerticalOscillationCm: sessionData.avg_vertical_oscillation_cm ?? sessionData.avgVerticalOscillationCm ?? null,
      avgGroundContactTimeMs: sessionData.avg_ground_contact_time_ms ?? sessionData.avgGroundContactTimeMs ?? null,
      avgStrideLengthM: sessionData.avg_stride_length_m ?? sessionData.avgStrideLengthM ?? null,
      avgGroundContactBalancePct: sessionData.avg_ground_contact_balance_pct ?? sessionData.avgGroundContactBalancePct ?? null,
      avgVerticalRatioPct: sessionData.avg_vertical_ratio_pct ?? sessionData.avgVerticalRatioPct ?? null,
    };
  }

  /**
   * Adapts Apple Health activity data from unified_athletic_data schema
   * Uses same schema as Garmin since data is stored in unified_athletic_data table
   */
  static fromAppleHealth(appleData: any): UnifiedSessionData {
    // Parse JSON fields (same structure as Garmin)
    const hrData = appleData.hr_data ? (typeof appleData.hr_data === 'string' ? JSON.parse(appleData.hr_data) : appleData.hr_data) : null;
    // Support BOTH field names: 'trackpoints' (DB column name) and 'gps_trackpoints' (legacy)
    const rawGps = appleData.trackpoints || appleData.gps_trackpoints;
    const gpsData = rawGps ? (typeof rawGps === 'string' ? JSON.parse(rawGps) : rawGps) : null;
    const splits = appleData.lap_splits || appleData.lap_data ?
      (typeof (appleData.lap_splits || appleData.lap_data) === 'string' ?
        JSON.parse(appleData.lap_splits || appleData.lap_data) :
        (appleData.lap_splits || appleData.lap_data)) : null;
    const samples = appleData.samples ? (typeof appleData.samples === 'string' ? JSON.parse(appleData.samples) : appleData.samples) : null;
    
    return {
      id: appleData.id,
      sessionType: this.mapHealthKitActivityType(appleData.activity_type),
      source: 'apple_health',
      completedAt: new Date(appleData.activity_date || appleData.created_at),
      totalDuration: appleData.duration_seconds || 0,
      calories: appleData.calories,
      distance: appleData.distance_meters,
      avgPace: appleData.avg_pace_seconds_per_km,
      avgSpeed: appleData.avg_speed_mps,
      maxSpeed: appleData.max_speed_mps,
      // S5-2: Prefer direct fields (matches Garmin/Strava pattern), fall back to hr_data blob
      avgHeartRate: appleData.average_heart_rate ?? appleData.avg_heart_rate ?? hrData?.avg_hr,
      maxHeartRate: appleData.max_heart_rate ?? hrData?.max_hr,
      // HRV: chest-strap RMSSD/SDNN from Cedar timer (migration 1338).
      hrvRmssdMs: appleData.hrv_rmssd_ms,
      hrvSdnnMs: appleData.hrv_sdnn_ms,
      hrvSampleCount: appleData.hrv_sample_count,
      elevationGain: appleData.elevation_gain_meters ?? appleData.elevation_gain,
      elevationLoss: appleData.elevation_loss_meters ?? appleData.elevation_loss,
      // Normalize GPS point field names (lat/lng → latitude/longitude) for hasGPSData() compatibility
      // 🔧 FIX 3 (2026-04-29): Read both legacy bare keys AND canonical-suffix
      // keys (altitude_m, timestamp_s, speed_mps, distance_m, heart_rate_bpm,
      // cadence_spm). Without the canonical fallbacks, Apple-source trackpoints
      // ingested through the W4/W9 canonical pipeline lost altitude/timestamp/
      // speed/HR per-point even though those values were stored in the DB.
      gpsData: gpsData ? gpsData.map((p: any) => ({
        latitude: p.latitude ?? p.lat,
        longitude: p.longitude ?? p.lng,
        altitude: p.altitude ?? p.altitude_m,
        timestamp: p.timestamp ?? p.timestamp_s,
        speed: p.speed ?? p.speed_mps,
        distanceFromStart: p.distanceFromStart ?? p.distance_from_start ?? p.distance ?? p.distance_m,
        heartRate: p.heartRate ?? p.heart_rate ?? p.heart_rate_bpm,
        cadence: p.cadence ?? p.cadence_spm,
      })) : null,
      samples: samples,
      splits: SessionDataAdapter.normalizeSplits(splits),
      avgCadence: appleData.avg_cadence,
      maxCadence: appleData.max_cadence,
      currentVo2Max: appleData.current_vo2_max ?? null,
      // Device attribution
      deviceModel: appleData.device_model || 'Apple Watch',
      deviceManufacturer: 'Apple',
      deviceName: appleData.device_name,
      // W6 (2026-04-28)
      dataCompletenessTier: appleData.data_completeness_tier ?? null,
      // W11 (2026-04-28): Apple Watch S7+ running form metrics
      // (HKQuantityTypeIdentifier runningGroundContactTime /
      // runningStrideLength / runningVerticalOscillation /
      // runningPower) — surfaced via the W5 Tier 4 bridge into the
      // sample stream; W9 aggregates per-sample into avg_*.
      avgVerticalOscillationCm: appleData.avg_vertical_oscillation_cm ?? null,
      avgGroundContactTimeMs: appleData.avg_ground_contact_time_ms ?? null,
      avgStrideLengthM: appleData.avg_stride_length_m ?? null,
      avgGroundContactBalancePct: appleData.avg_ground_contact_balance_pct ?? null,
      avgVerticalRatioPct: appleData.avg_vertical_ratio_pct ?? null,
    };
  }

  /**
   * Adapts Android Health Connect activity data from unified_athletic_data schema
   * Uses same schema as Garmin since data is stored in unified_athletic_data table
   */
  static fromAndroidHealth(androidData: any): UnifiedSessionData {
    // Parse JSON fields (same structure as Garmin)
    const hrData = androidData.hr_data ? (typeof androidData.hr_data === 'string' ? JSON.parse(androidData.hr_data) : androidData.hr_data) : null;
    // Support BOTH field names: 'trackpoints' (DB column name) and 'gps_trackpoints' (legacy)
    const rawGps = androidData.trackpoints || androidData.gps_trackpoints;
    const gpsData = rawGps ? (typeof rawGps === 'string' ? JSON.parse(rawGps) : rawGps) : null;
    const splits = androidData.lap_splits || androidData.lap_data ?
      (typeof (androidData.lap_splits || androidData.lap_data) === 'string' ?
        JSON.parse(androidData.lap_splits || androidData.lap_data) :
        (androidData.lap_splits || androidData.lap_data)) : null;
    const samples = androidData.samples ? (typeof androidData.samples === 'string' ? JSON.parse(androidData.samples) : androidData.samples) : null;
    
    return {
      id: androidData.id,
      sessionType: this.mapHealthConnectActivityType(androidData.activity_type),
      source: 'android_health',
      completedAt: new Date(androidData.activity_date || androidData.created_at),
      totalDuration: androidData.duration_seconds || 0,
      calories: androidData.calories,
      distance: androidData.distance_meters,
      avgPace: androidData.avg_pace_seconds_per_km,
      avgSpeed: androidData.avg_speed_mps,
      maxSpeed: androidData.max_speed_mps,
      // S5-2: Prefer direct fields (matches Garmin/Strava pattern), fall back to hr_data blob
      avgHeartRate: androidData.average_heart_rate ?? androidData.avg_heart_rate ?? hrData?.avg_hr,
      maxHeartRate: androidData.max_heart_rate ?? hrData?.max_hr,
      // HRV: chest-strap RMSSD/SDNN from Cedar timer (migration 1338).
      hrvRmssdMs: androidData.hrv_rmssd_ms,
      hrvSdnnMs: androidData.hrv_sdnn_ms,
      hrvSampleCount: androidData.hrv_sample_count,
      elevationGain: androidData.elevation_gain_meters ?? androidData.elevation_gain,
      elevationLoss: androidData.elevation_loss_meters ?? androidData.elevation_loss,
      // Normalize GPS point field names (lat/lng → latitude/longitude) for hasGPSData() compatibility
      gpsData: gpsData ? gpsData.map((p: any) => ({
        latitude: p.latitude ?? p.lat,
        longitude: p.longitude ?? p.lng,
        altitude: p.altitude,
        timestamp: p.timestamp,
        speed: p.speed,
        distanceFromStart: p.distanceFromStart ?? p.distance_from_start ?? p.distance,
      })) : null,
      samples: samples,
      splits: SessionDataAdapter.normalizeSplits(splits),
      avgCadence: androidData.avg_cadence,
      maxCadence: androidData.max_cadence,
      currentVo2Max: androidData.current_vo2_max ?? null,
      // Device attribution
      deviceModel: androidData.device_model,
      deviceManufacturer: androidData.device_manufacturer,
      deviceName: androidData.device_name,
      // W6 (2026-04-28)
      dataCompletenessTier: androidData.data_completeness_tier ?? null,
      // W11 (2026-04-28): running dynamics from Health Connect
      // (when the paired Android device captures them).
      avgVerticalOscillationCm: androidData.avg_vertical_oscillation_cm ?? null,
      avgGroundContactTimeMs: androidData.avg_ground_contact_time_ms ?? null,
      avgStrideLengthM: androidData.avg_stride_length_m ?? null,
      avgGroundContactBalancePct: androidData.avg_ground_contact_balance_pct ?? null,
      avgVerticalRatioPct: androidData.avg_vertical_ratio_pct ?? null,
    };
  }

  /**
   * Maps Garmin activity types to Cedar session types
   */
  private static mapGarminActivityType(garminType: string): 'cardio' | 'strength' | 'mobility' | 'yoga' | 'hiit' {
    const typeMap: Record<string, 'cardio' | 'strength' | 'mobility' | 'yoga' | 'hiit'> = {
      'running': 'cardio',
      'cycling': 'cardio',
      'swimming': 'cardio',
      'walking': 'cardio',
      'hiking': 'cardio',
      'strength_training': 'strength',
      'cardio_training': 'hiit',
      'hiit': 'hiit',
      'yoga': 'yoga',
      'flexibility_training': 'mobility',
      'other': 'cardio', // Default
    };
    
    return typeMap[garminType?.toLowerCase()] || 'cardio';
  }

  /**
   * Maps Strava activity types to Cedar session types
   */
  private static mapStravaActivityType(stravaType: string): 'cardio' | 'strength' | 'mobility' | 'yoga' | 'hiit' {
    const typeMap: Record<string, 'cardio' | 'strength' | 'mobility' | 'yoga' | 'hiit'> = {
      'Run': 'cardio',
      'Ride': 'cardio',
      'Swim': 'cardio',
      'Walk': 'cardio',
      'Hike': 'cardio',
      'WeightTraining': 'strength',
      'Workout': 'hiit',
      'Yoga': 'yoga',
      'other': 'cardio', // Default
    };
    
    return typeMap[stravaType] || 'cardio';
  }

  /**
   * Maps Apple HealthKit activity types to Cedar session types
   */
  private static mapHealthKitActivityType(healthKitType: string): 'cardio' | 'strength' | 'mobility' | 'yoga' | 'hiit' {
    const typeMap: Record<string, 'cardio' | 'strength' | 'mobility' | 'yoga' | 'hiit'> = {
      'running': 'cardio',
      'cycling': 'cardio',
      'swimming': 'cardio',
      'walking': 'cardio',
      'hiking': 'cardio',
      'traditionalStrengthTraining': 'strength',
      'functionalStrengthTraining': 'strength',
      'crossTraining': 'hiit',
      'highIntensityIntervalTraining': 'hiit',
      'yoga': 'yoga',
      'flexibility': 'mobility',
      'other': 'cardio', // Default
    };
    
    return typeMap[healthKitType?.toLowerCase()] || 'cardio';
  }

  /**
   * Maps Android Health Connect activity types to Cedar session types
   */
  private static mapHealthConnectActivityType(healthConnectType: string): 'cardio' | 'strength' | 'mobility' | 'yoga' | 'hiit' {
    const typeMap: Record<string, 'cardio' | 'strength' | 'mobility' | 'yoga' | 'hiit'> = {
      'running': 'cardio',
      'cycling': 'cardio',
      'swimming': 'cardio',
      'walking': 'cardio',
      'hiking': 'cardio',
      'strength_training': 'strength',
      'workout': 'hiit',
      'high_intensity_interval_training': 'hiit',
      'yoga': 'yoga',
      'stretching': 'mobility',
      'other': 'cardio', // Default
    };
    
    return typeMap[healthConnectType?.toLowerCase()] || 'cardio';
  }

  /**
   * Auto-detects source from data structure and adapts accordingly
   */
  static adapt(rawData: any): UnifiedSessionData {
    // Check for source indicators
    if (rawData.source_type === 'garmin' || rawData.source === 'garmin') {
      return this.fromGarmin(rawData);
    } else if (rawData.source_type === 'strava' || rawData.source === 'strava') {
      return this.fromStrava(rawData);
    // S5-3: Added 'healthkit' and 'health_connect' source types (used by native health sync endpoints)
    } else if (rawData.source_type === 'apple_health' || rawData.source_type === 'ios' || rawData.source_type === 'healthkit' || rawData.source === 'apple_health' || rawData.source === 'healthkit') {
      return this.fromAppleHealth(rawData);
    } else if (rawData.source_type === 'android_health' || rawData.source_type === 'google_fit' || rawData.source_type === 'health_connect' || rawData.source === 'android_health' || rawData.source === 'health_connect') {
      return this.fromAndroidHealth(rawData);
    } else if (rawData.source_type === 'google_health' || rawData.source === 'google_health') {
      // Google Health (Pixel Watch + Fitbit) persists to unified_athletic_data
      // in the same Garmin-shaped snake_case schema, so reuse fromGarmin to read
      // the fields — but stamp the honest source so attribution/badges/capabilities
      // resolve to "Google Health · <device>", not Garmin. Parity hardening #25.
      return { ...this.fromGarmin(rawData), source: 'google_health' };
    } else if (rawData.sessionData || rawData.source === 'cedar') {
      return this.fromCedar(rawData);
    }
    
    // Fallback: Try to infer from structure
    if (rawData.gps_trackpoints || rawData.hr_data || rawData.power_data) {
      // Looks like unified_athletic_data schema
      return this.fromGarmin(rawData); // Use same adapter for unified schema
    }
    
    // Default to Cedar format
    return this.fromCedar(rawData);
  }
}

// Export convenience instance for backward compatibility
export const sessionDataAdapter = SessionDataAdapter;
